import { useEffect, useState, useId } from "react";
import { T } from "theme/index";
import { fmtIDR, fmtShort } from "utils/format";

// ── Responsive hook ───────────────────────────────────────────────────────────
function detectIsMobile() {
  const isTouch = window.matchMedia("(pointer: coarse)").matches;
  return isTouch ? window.innerWidth < 1024 : window.innerWidth < 768;
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(detectIsMobile());
  useEffect(() => {
    const fn = () => setIsMobile(detectIsMobile());
    window.addEventListener("resize", fn);
    window.addEventListener("orientationchange", fn);
    return () => {
      window.removeEventListener("resize", fn);
      window.removeEventListener("orientationchange", fn);
    };
  }, []);
  return isMobile;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
export function Card({ children, style }) {
  return <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, boxShadow:`0 1px 3px rgba(0,0,0,0.08)`, ...style }}>{children}</div>;
}

export function Badge({ label, color }) {
  return <span style={{ background:color+"22", color, border:`1px solid ${color}33`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700, letterSpacing:"0.05em" }}>{label}</span>;
}

// MoM badge dengan warna sesuai konteks (up=good or up=bad)
export function MoMBadgeCtx({ current, prev, upGood=true }) {
  if (!prev || prev === 0) return null;
  const delta = current - prev;
  const up  = delta > 0;                          // arah dari SELISIH, bukan hasil bagi (biar gak kebalik tandanya)
  const pct = (delta / Math.abs(prev)) * 100;      // penyebut di-mutlak-kan, biar angkanya gak ikut kebalik
  const col = up ? (upGood ? T.green : T.red) : (upGood ? T.red : T.green);
  return (
    <span style={{ fontSize:10, fontWeight:700, color:col, display:"inline-flex", alignItems:"center", gap:2 }}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}% vs bln lalu
    </span>
  );
}

export function SumCard({ label, value, color, prefix="", prev=null, upGood=true }) {
  const isMobile = useIsMobile();
  const absVal   = Math.abs(value);
  const formatted = (value < 0 ? "-" : (prefix || "")) + fmtIDR(absVal);
  const len = formatted.length;
  const fontSize = isMobile
    ? (len > 17 ? 11 : len > 14 ? 13 : len > 11 ? 15 : 17)
    : (len > 17 ? 14 : len > 14 ? 16 : len > 11 ? 18 : 20);
  return (
    <div style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      borderTop: `3px solid ${color}`,
      padding: "14px 16px",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Subtle tint in corner */}
      <div style={{ position:"absolute", top:0, right:0, width:60, height:60,
        background:`radial-gradient(circle at 100% 0%, ${color}12, transparent 70%)`,
        pointerEvents:"none" }} />
      <div style={{ color:T.muted, fontSize:10, fontWeight:700, letterSpacing:"0.1em",
        textTransform:"uppercase", marginBottom:6 }}>{label}</div>
      <div style={{ color:T.text, fontSize, fontWeight:800, fontFamily:"monospace",
        fontVariantNumeric:"tabular-nums", lineHeight:1, marginBottom:4,
        whiteSpace:"nowrap", overflow:"hidden" }}>{formatted}</div>
      {prev !== null && <MoMBadgeCtx current={value} prev={prev} upGood={upGood} />}
    </div>
  );
}

export function FieldLabel({ children, htmlFor }) {
  return <label htmlFor={htmlFor} style={{ display:"block", color:T.muted, fontSize:11, fontWeight:700, letterSpacing:"0.07em", marginBottom:6, textTransform:"uppercase" }}>{children}</label>;
}

export function Input({ label, id, ...props }) {
  const autoId = useId();
  const fieldId = id || autoId;
  return (
    <div style={{ marginBottom:14 }}>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
      <input id={fieldId} {...props} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:16, padding:"11px 12px", outline:"none", boxSizing:"border-box", WebkitAppearance:"none", ...props.style }} />
    </div>
  );
}

export function Sel({ label, options, id, ...props }) {
  const autoId = useId();
  const fieldId = id || autoId;
  return (
    <div style={{ marginBottom:14 }}>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
      <select id={fieldId} {...props} style={{ width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:16, padding:"11px 12px", outline:"none", cursor:"pointer", boxSizing:"border-box", WebkitAppearance:"none", ...props.style }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

export function Btn({ children, onClick, variant="primary", style, disabled }) {
  const vv = {
    primary: { background:T.gold, color:T.bg, border:"none" },
    ghost:   { background:"transparent", color:T.muted, border:`1px solid ${T.border}` },
    danger:  { background:T.red+"18", color:T.red, border:`1px solid ${T.red}30` },
    surface: { background:T.surface, color:T.text, border:`1px solid ${T.border}` },
  };
  return (
    <button onClick={onClick} disabled={disabled} className="jf-btn" style={{ ...vv[variant], borderRadius:8, padding:"10px 16px", fontSize:14, cursor:disabled?"not-allowed":"pointer", fontWeight:700, display:"flex", alignItems:"center", gap:6, opacity:disabled?0.45:1, minHeight:44, transition:"filter 0.15s, transform 0.1s, opacity 0.15s", WebkitTapHighlightColor:"transparent", ...style }}>
      {children}
    </button>
  );
}

export function Skeleton({ width="100%", height=14, style }) {
  return <div className="jf-skeleton" style={{ width, height, ...style }} aria-hidden="true" />;
}

export function DashboardSkeleton() {
  return (
    <div aria-busy="true" aria-label="Memuat data">
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10, marginBottom:16 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"14px 16px" }}>
            <Skeleton width={60} height={9} style={{ marginBottom:10 }} />
            <Skeleton width="70%" height={17} />
          </div>
        ))}
      </div>
      <Card style={{ padding:16, marginBottom:14 }}>
        <Skeleton width={140} height={13} style={{ marginBottom:14 }} />
        <Skeleton height={180} />
      </Card>
      <Card>
        <div style={{ padding:16 }}><Skeleton width={160} height={13} /></div>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 16px", borderTop:`1px solid ${T.border}` }}>
            <Skeleton width={36} height={36} style={{ borderRadius:9, flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <Skeleton width="55%" height={12} style={{ marginBottom:6 }} />
              <Skeleton width="35%" height={10} />
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export function Toast({ message, type="info", onDismiss }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => onDismiss?.(), 4000);
    return () => clearTimeout(t);
  }, [message, onDismiss]);

  if (!message) return null;
  const col = type === "error" ? T.red : type === "success" ? T.green : T.gold;
  return (
    <div role="status" aria-live="polite" style={{
      position:"fixed", left:"50%", bottom:"max(20px, env(safe-area-inset-bottom))", transform:"translateX(-50%)",
      zIndex:3000, maxWidth:"min(92vw,420px)", display:"flex", alignItems:"center", gap:10,
      background:T.card, border:`1px solid ${T.border}`,
      borderRadius:10, padding:"12px 14px", boxShadow:"0 8px 24px rgba(0,0,0,0.35)",
    }}>
      <span style={{ width:8, height:8, borderRadius:"50%", background:col, flexShrink:0 }} />
      <span style={{ color:T.text, fontSize:13, flex:1 }}>{message}</span>
      <button onClick={onDismiss} aria-label="Tutup notifikasi" className="jf-btn" style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:18, lineHeight:1, padding:2, borderRadius:6, flexShrink:0 }}>×</button>
    </div>
  );
}

export function Modal({ title, onClose, children, wide }) {
  const isMobile = useIsMobile();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{ position:"fixed", inset:0, zIndex:2000, background:"rgba(4,7,18,0.9)", display:"flex", alignItems:isMobile?"flex-end":"center", justifyContent:"center", padding:isMobile?0:16 }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="jf-modal-title" style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:isMobile?"16px 16px 0 0":"16px", width:isMobile?"100%":`min(96vw,${wide?680:520}px)`, maxHeight:isMobile?"92vh":"90vh", overflow:"auto", display:"flex", flexDirection:"column" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"16px 18px 12px", borderBottom:`1px solid ${T.border}`, flexShrink:0 }}>
          <span id="jf-modal-title" style={{ color:T.text, fontWeight:700, fontSize:16 }}>{title}</span>
          <button onClick={onClose} aria-label="Tutup" className="jf-btn" style={{ background:"none", border:"none", color:T.muted, cursor:"pointer", fontSize:24, lineHeight:1, padding:"0 4px", borderRadius:6, WebkitTapHighlightColor:"transparent" }}>×</button>
        </div>
        <div style={{ padding:"16px 18px 24px", overflow:"auto" }}>{children}</div>
      </div>
    </div>
  );
}

export function CTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"8px 12px", fontSize:12, color:T.text }}>
      <div style={{ color:T.muted, marginBottom:4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display:"flex", gap:8, alignItems:"center" }}>
          <div style={{ width:8, height:8, borderRadius:2, background:p.color }} />
          <span style={{ color:T.muted }}>{p.name==="income"?"Pemasukan":p.name==="expense"?"Pengeluaran":p.name==="investasi"?"Investasi":p.name}:</span>
          <span style={{ fontFamily:"monospace", fontVariantNumeric:"tabular-nums" }}>{fmtShort(p.value)}</span>
        </div>
      ))}
    </div>
  );
}
